import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AdminButtonBG from '../../assets/images/AdminButtonBG.webp';

/**
 * CustomButton
 * A lightweight button component that uses a native <button> element and
 * accepts Tailwind `className`, an optional `icon`, `text`, and an `onClick` handler.
 * It intentionally does not depend on Chakra UI.
 */
const CustomButton = React.forwardRef((props, ref) => {
    const {
        className = '',
        icon = null,
        text = null,
        onClick = () => { },
        disabled = false,
        loading = false,
        type = 'button',
        ariaLabel = null,
        primary = false,
        ...rest
    } = props;

    const [hovered, setHovered] = useState(false);

    const combinedClassName = `${className}`.trim();

    const style = {};
    if (primary) {
        style.backgroundImage = `url(${AdminButtonBG})`;
        style.backgroundSize = 'cover';
        style.backgroundRepeat = 'no-repeat';
    }

    const isDisabled = disabled || loading;
    const classes = `${combinedClassName} hover:shadow-xl${isDisabled ? ' opacity-50 cursor-not-allowed' : ''}`.trim();

    return (
        <button
            ref={ref}
            type={type}
            className={classes}
            style={style}
            onClick={isDisabled ? undefined : onClick}
            disabled={isDisabled}
            aria-label={ariaLabel}
            aria-disabled={isDisabled}
            aria-busy={loading}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            {...rest}
        >
            {loading ? (
                <span className="inline-flex items-center gap-2">
                    <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    {text && <span>{text}</span>}
                </span>
            ) : icon ? (
                <span className="inline-flex items-center gap-2">
                    <span className="inline-flex">{icon}</span>
                    {text && <span className='leading-none'>{text}</span>}
                </span>
            ) : (
                <>{text}</>
            )}
        </button>
    );
});

CustomButton.displayName = 'CustomButton';

CustomButton.propTypes = {
    className: PropTypes.string,
    icon: PropTypes.node,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    ariaLabel: PropTypes.string,
    primary: PropTypes.bool,
};

export default CustomButton;
